import {
  ComboboxItem,
  ComboboxLikeRenderOptionInput,
  Group,
  Stack,
  Text,
} from "@mantine/core"
import type { ComboboxData } from "@mantine/core"
import { useDebouncedValue, useDisclosure } from "@mantine/hooks"
import { keepPreviousData } from "@tanstack/react-query"
import { CountryLabel, LabelWithTooltip, Select, SelectProps } from "@tfg/ui"
import { useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { queryClient } from "~/backend/queryClient"
import { IconManufacturing } from "~/components/icons/activities/IconManufacturing"
import {
  type FindProductionSitesForSupplierQueryResponse,
  type FindProductionSitesForSupplierResponseDto,
  findProductionSitesForSupplierQueryKey,
  useFindProductionSitesForSupplier,
} from "~/gen/api"
import { usePaginatedQuery } from "~/hooks/usePaginatedQuery"
import { EditProductionSiteDrawer } from "~/pages/SupplierPortal/ProductionSites/components/EditProductionSiteDrawer.tsx"
import { useAuth } from "~/store/auth-context"
import { COUNTRIES } from "~/utils/countries"
import { formatAddress } from "~/utils/format-address"

type SelectedProductionSite = Pick<
  FindProductionSitesForSupplierResponseDto,
  "id" | "name" | "country" | "street" | "city" | "postalCode"
>

type ProductionSiteOption = ComboboxItem & {
  productionSite?: SelectedProductionSite
}

interface Props extends Partial<SelectProps> {
  productionSiteId: string | null
  onProductionSiteSelected: (productionSiteId: string | null) => void
  disabled?: boolean
  isRequired?: boolean
  tooltip?: string
}

export const ProductionSiteSelect = ({
  productionSiteId,
  onProductionSiteSelected,
  disabled,
  isRequired,
  tooltip,
}: Props) => {
  const { t } = useTranslation(["traceability"])
  const [modalOpen, modalActions] = useDisclosure(false)
  const { isAdmin } = useAuth()
  const [enabled, setEnabled] = useState(false)
  const [search, setSearch] = useState("")
  const [debouncedSearch] = useDebouncedValue(search, 300)
  const selectedProductionSiteRef = useRef<SelectedProductionSite | null>(null)
  const isQueryEnabled = enabled || productionSiteId !== null
  const queryParams = useMemo(
    () => ({
      filters: {
        search: debouncedSearch || undefined,
      },
    }),
    [debouncedSearch]
  )

  const {
    data: productionSitesData,
    isPending,
    cursorBasedPagination,
    pageBasedPagination,
  } = usePaginatedQuery<
    typeof useFindProductionSitesForSupplier,
    FindProductionSitesForSupplierQueryResponse
  >(
    useFindProductionSitesForSupplier,
    queryParams,
    {
      query: {
        placeholderData: keepPreviousData,
        enabled: isQueryEnabled,
        staleTime: 5 * 60 * 1000,
      },
    },
    {
      initialPagination: {
        skip: 0,
        take: 1,
      },
      fetchAllUsingCount: true,
    }
  )

  const selectOptions: ComboboxData = useMemo(() => {
    if (!Array.isArray(productionSitesData?.data)) {
      return []
    }

    if (productionSiteId === null) {
      selectedProductionSiteRef.current = null
    } else {
      const selectedProductionSiteFromResponse = productionSitesData.data.find(
        (item) => item.id === productionSiteId
      )
      if (selectedProductionSiteFromResponse !== undefined) {
        selectedProductionSiteRef.current = {
          id: selectedProductionSiteFromResponse.id,
          name: selectedProductionSiteFromResponse.name,
          country: selectedProductionSiteFromResponse.country,
          street: selectedProductionSiteFromResponse.street,
          city: selectedProductionSiteFromResponse.city,
          postalCode: selectedProductionSiteFromResponse.postalCode,
        }
      }
    }

    const options = productionSitesData.data.map<ProductionSiteOption>(
      (item: FindProductionSitesForSupplierResponseDto) => ({
        label: item.name,
        value: item.id,
        productionSite: {
          id: item.id,
          name: item.name,
          country: item.country,
          street: item.street,
          city: item.city,
          postalCode: item.postalCode,
        },
      })
    )

    const selectedProductionSite =
      productionSiteId !== null &&
      selectedProductionSiteRef.current?.id === productionSiteId
        ? selectedProductionSiteRef.current
        : null
    if (
      selectedProductionSite !== null &&
      options.every((option) => option.value !== selectedProductionSite.id)
    ) {
      options.push({
        value: selectedProductionSite.id,
        label: selectedProductionSite.name,
        productionSite: {
          id: selectedProductionSite.id,
          name: selectedProductionSite.name,
          country: selectedProductionSite.country,
          street: selectedProductionSite.street,
          city: selectedProductionSite.city,
          postalCode: selectedProductionSite.postalCode,
        },
      })
    }

    return options
  }, [productionSiteId, productionSitesData?.data])

  const isSelectLoading = isQueryEnabled && isPending
  return (
    <>
      <Select
        data={selectOptions}
        value={productionSiteId}
        onChange={(value) => {
          onProductionSiteSelected(value && value !== "$create" ? value : null)
        }}
        onFocus={() => setEnabled(true)}
        onDropdownOpen={() => {
          setEnabled(true)
        }}
        onSearchChange={setSearch}
        searchValue={search}
        searchable
        clearable
        creatable
        onCreate={() => modalActions.open()}
        createLabel={t("productionSiteSelect.labels.addNewProductionSite")}
        placeholder={t("productionSiteSelect.placeholders.productionSite")}
        disabled={disabled}
        isLoading={isSelectLoading}
        error={!productionSiteId && !disabled}
        copyableValue={isAdmin ? "productionSiteId" : undefined}
        isAdmin={isAdmin}
        label={
          <LabelWithTooltip
            justify="space-between"
            label={t("productionSiteSelect.labels.productionSite")}
            hoverHelperText={
              tooltip && tooltip.length > 0 ? tooltip : undefined
            }
            withAsterisk={isRequired}
          />
        }
        leftSection={<IconManufacturing size={16} />}
        customRender={({
          option,
          checked,
        }: ComboboxLikeRenderOptionInput<ProductionSiteOption>) => {
          const productionSite = option.productionSite
          return (
            <Group
              justify="space-between"
              data-checked={checked ? "true" : undefined}
              p="sm"
              w="100%"
            >
              <Stack gap={0}>
                <Text fw={700}>{productionSite?.name ?? option.label}</Text>
                {productionSite?.country && (
                  <Group gap="xs">
                    <CountryLabel
                      countryIso3166={productionSite.country}
                      width={12}
                      countriesMapper={COUNTRIES}
                    />
                    <Text size="xs" style={{ width: "100%" }}>
                      {formatAddress(productionSite)}
                    </Text>
                  </Group>
                )}
              </Stack>
            </Group>
          )
        }}
      />
      {modalOpen && (
        <EditProductionSiteDrawer
          opened={modalOpen}
          onClose={modalActions.close}
          onProductionSiteCreated={(productionSiteIdCreated) => {
            void queryClient.invalidateQueries({
              queryKey: findProductionSitesForSupplierQueryKey({
                ...queryParams,
                pagination: cursorBasedPagination,
              }),
            })
            onProductionSiteSelected(productionSiteIdCreated)
          }}
          pagination={pageBasedPagination}
        />
      )}
    </>
  )
}
